// src/components/Portfolio.js
import React from 'react';
import { Languages, ArrowLeft, Monitor, Brain, UserSquare2, Mail } from 'lucide-react';
import ProjectCard from './ProjectCard';
import BlogPost from './BlogPost';
import { projects } from '../data/projects';
import { blogPosts } from '../data/blogPosts';
import { Link } from 'react-router-dom';
import { getStoredLanguage, setStoredLanguage } from '../utils/languageUtils';



// Content object with translations
const content = {
  en: {
    nav: {
      work: "Work",
      thoughts: "Thoughts",
      about: "About",
      contact: "Contact"
    },
    hero: {
      title: "Crafting AI Products with Human-Centered Design",
      subtitle: "I'm an AI product designer focused on creating intuitive and impactful experiences at the intersection of artificial intelligence and human needs.",
      viewWork: "View Work",
      getInTouch: "Get in Touch"
    },
    work: {
      title: "Featured Work",
      projects: [
        {
          title: "From Zero to Launch with AI",
          description: "A journey of building a fully functional website in 3 days using AI assistance and MCP methodology.",
          cta: "View Case Study"
        }
      ]
    },
    thoughts: {
      title: "Recent Thoughts",
      posts: [
        {
          date: "June 12, 2024",
          title: "The Future of AI in Design Systems",
          description: "Exploring how artificial intelligence will transform the way we create and maintain design systems."
        }
      ]
    },
    about: {
      title: "About Me",
      content: "I'm passionate about creating AI products that enhance human capabilities while maintaining simplicity and accessibility. With experience in both design and artificial intelligence, I bridge the gap between complex technology and user needs.",
      skills: ["UI/UX Design", "AI/ML", "User Research", "Communication"]
    },
    contact: {
      title: "Let's Connect",
      subtitle: "Interested in collaborating or just want to chat about AI and design? I'd love to hear from you!",
      cta: "Get in Touch"
    },
    footer: {
      copyright: "© 2024 Your Name. All rights reserved."
    }
  },
  zh: {
    nav: {
      work: "作品",
      thoughts: "思考",
      about: "关于",
      contact: "联系"
    },
    hero: {
      title: "以人为本的AI产品设计",
      subtitle: "作为AI产品设计师，我专注于在人工智能与人性化需求的交叉点上，创造直观且具有影响力的体验。",
      viewWork: "查看作品",
      getInTouch: "联系我"
    },
    work: {
      title: "精选作品",
      projects: [
        {
          title: "从零到成功：AI辅助开发之旅",
          description: "使用AI辅助和MCP方法论，在3天内构建完整功能网站的历程。",
          cta: "查看案例"
        }
      ]
    },
    thoughts: {
      title: "近期思考",
      posts: [
        {
          date: "2024年6月12日",
          title: "AI在设计系统中的未来",
          description: "探索人工智能将如何改变我们创建和维护设计系统的方式。"
        }
      ]
    },
    about: {
      title: "关于我",
      content: "我热衷于创造能够增强人类能力的AI产品，同时保持简单易用和可访问性。凭借在设计和人工智能领域的经验，我致力于架起复杂技术与用户需求之间的桥梁。",
      skills: ["UI/UX设计", "AI/机器学习", "用户研究", "沟通协作"]
    },
    contact: {
      title: "让我们联系",
      subtitle: "想要合作或只是想聊聊AI和设计？我很期待与您交流！",
      cta: "联系我"
    },
    footer: {
      copyright: "© 2024 您的名字。保留所有权利。"
    }
  }
};

const Portfolio = () => {
  
  
  const [language, setLanguage] = React.useState(getStoredLanguage());

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    setStoredLanguage(newLanguage);
  };
  const t = content[language];
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <span className="text-xl font-semibold">Your Name</span>
            </div>
            <div className="hidden sm:flex items-center space-x-8">
              <a href="#work" className="text-gray-600 hover:text-gray-900">{t.nav.work}</a>
              <a href="#thoughts" className="text-gray-600 hover:text-gray-900">{t.nav.thoughts}</a>
              <a href="#about" className="text-gray-600 hover:text-gray-900">{t.nav.about}</a>
              <a href="#contact" className="text-gray-600 hover:text-gray-900">{t.nav.contact}</a>
              <button
      onClick={() => handleLanguageChange(language === 'en' ? 'zh' : 'en')}
      className="flex items-center gap-2 px-3 py-1 rounded border border-gray-200 hover:border-gray-300 text-sm"
    >
      <Languages className="w-4 h-4" />
      {language === 'en' ? '中文' : 'English'}
    </button>
             
            </div>
          </div>
        </div>
      </nav>
      {/* Hero Section */}
      <section className="pt-32 pb-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            {t.hero.title}
          </h1>
          <p className="text-xl text-gray-600 mb-12 leading-relaxed">
            {t.hero.subtitle}
          </p>
          <div className="flex justify-center gap-4">
            <a 
              href="#work" 
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t.hero.viewWork}
            </a>
            <a 
              href="#contact" 
              className="px-8 py-3 border border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
            >
              {t.hero.getInTouch}
            </a>
          </div>
        </div>
      </section>
      {/* Featured Work */}
      <section id="work" className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">{t.work.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {t.work.projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="h-64 bg-gray-100" />
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2">{project.title}</h3>
                  <p className="text-gray-600 mb-4">
                    {project.description}
                  </p>
                  <Link 
                    to={`/case-study/${index + 1}`} 
                    className="text-blue-600 hover:text-blue-700"
                  >
                    {project.cta} →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Thoughts/Blog */}
      <section id="thoughts" className="py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">{t.thoughts.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.thoughts.posts.map((post, index) => (
              <article key={index} className="flex flex-col">
                <time className="text-sm text-gray-500 mb-2">{post.date}</time>
                <h3 className="text-xl font-semibold mb-2">{post.title}</h3>
                <p className="text-gray-600 mb-4">
                  {post.description}
                </p>
                <a href="#" className="text-blue-600 hover:text-blue-700 mt-auto">
                  {language === 'en' ? 'Read More →' : '阅读更多 →'}
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">{t.about.title}</h2>
              <p className="text-gray-600 mb-6">
                {t.about.content}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {t.about.skills.map((skill, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-600 rounded-full" />
                    <span>{skill}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="h-96 bg-gray-200 rounded-xl" />
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t.contact.title}</h2>
          <p className="text-gray-600 mb-8">
            {t.contact.subtitle}
          </p>
          <a 
            href="mailto:your.email@example.com" 
            className="inline-flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t.contact.cta}
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-500">
          {t.footer.copyright}
        </div>
      </footer>
      {/* Rest of the Portfolio sections with translations... */}
      {/* You can follow the same pattern as we did in the case study */}
      
    </div>
  );
};

export default Portfolio;